import React, { useRef, useState, useCallback, useEffect } from 'react'

export const SelectionArea = ({ children, onCapture }) => {
  const containerRef = useRef(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 })

  const handleMouseDown = useCallback((e) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    setStartPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
    setCurrentPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
    setIsSelecting(true)
  }, [])

  const handleMouseUp = useCallback(async () => {
    if (!isSelecting || !containerRef.current) return

    setIsSelecting(false)

    const selectionWidth = Math.abs(currentPos.x - startPos.x)
    const selectionHeight = Math.abs(currentPos.y - startPos.y)

    // Ignorar selecciones muy pequeñas
    if (selectionWidth < 10 || selectionHeight < 10) return

    const x = Math.min(startPos.x, currentPos.x)
    const y = Math.min(startPos.y, currentPos.y)

    try {
      const pdfCanvas = containerRef.current.querySelector('canvas')

      if (!pdfCanvas) {
        console.error('No se encontró el canvas del PDF')
        return
      }

      // Calcular la escala entre el tamaño del canvas y el tamaño mostrado
      const scaleX = pdfCanvas.width / pdfCanvas.offsetWidth
      const scaleY = pdfCanvas.height / pdfCanvas.offsetHeight

      // Obtener la posición del canvas dentro del contenedor
      const canvasRect = pdfCanvas.getBoundingClientRect()
      const containerRect = containerRef.current.getBoundingClientRect()

      const canvasOffsetX = canvasRect.left - containerRect.left
      const canvasOffsetY = canvasRect.top - containerRect.top

      // Ajustar las coordenadas de selección al canvas
      const adjustedX = (x - canvasOffsetX) * scaleX
      const adjustedY = (y - canvasOffsetY) * scaleY
      const adjustedWidth = selectionWidth * scaleX
      const adjustedHeight = selectionHeight * scaleY

      // Crear un nuevo canvas para el recorte
      const clipCanvas = document.createElement('canvas')
      clipCanvas.width = adjustedWidth
      clipCanvas.height = adjustedHeight
      const ctx = clipCanvas.getContext('2d')

      if (!ctx) {
        console.error('No se pudo obtener el contexto del canvas')
        return
      }

      // Copiar la región seleccionada del canvas del PDF con las coordenadas ajustadas
      ctx.drawImage(
        pdfCanvas,
        adjustedX,
        adjustedY,
        adjustedWidth,
        adjustedHeight, // origen en el canvas fuente
        0,
        0,
        adjustedWidth,
        adjustedHeight // destino en el nuevo canvas
      )

      const imageData = clipCanvas.toDataURL('image/png')
      onCapture(imageData)
    } catch (error) {
      console.error('Error al capturar el recorte:', error)
    }
  }, [isSelecting, startPos, currentPos, onCapture])

  // Agregar eventos globales para manejar el movimiento y liberación del mouse
  // incluso cuando el cursor sale del contenedor
  useEffect(() => {
    if (!isSelecting) return

    const handleGlobalMouseMove = (e) => {
      if (!containerRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      
      // Calcular la posición del mouse relativa al contenedor
      let x = e.clientX - rect.left
      let y = e.clientY - rect.top

      // Limitar las coordenadas a los bordes del contenedor (clamp)
      x = Math.max(0, Math.min(x, rect.width))
      y = Math.max(0, Math.min(y, rect.height))

      setCurrentPos({ x, y })
    }

    const handleGlobalMouseUp = () => {
      handleMouseUp()
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isSelecting, handleMouseUp])

  const selectionStyle = isSelecting
    ? {
        left: Math.min(startPos.x, currentPos.x),
        top: Math.min(startPos.y, currentPos.y),
        width: Math.abs(currentPos.x - startPos.x),
        height: Math.abs(currentPos.y - startPos.y),
      }
    : {}

  return (
    <div
      ref={containerRef}
      className="selection-area-container"
      onMouseDown={handleMouseDown}
    >
      {children}
      {isSelecting && (
        <div 
          className="selection-area-rectangle" 
          style={selectionStyle} 
        />
      )}
    </div>
  )
}

export default SelectionArea




